import { assertUnreachable, nonWhiteSpaceOr } from "@arti/utilities";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";

import type {
  DashboardAdminFeatureFlagDefinition,
  DashboardAdminFeatureFlagOverride,
  DashboardAdminFeatureFlagsListResponse,
  DashboardAdminOrganization,
} from "../../dashboard-client";

import { Alert } from "../common/Alert";
import { Button } from "../common/Button";
import { ConfirmationDialog } from "../common/ConfirmationDialog";
import { PanelSection } from "../common/PanelSection";

type FeatureFlagName = DashboardAdminFeatureFlagDefinition["flag_name"];
type OverrideSelection = "default" | "enabled" | "disabled";

interface PendingFeatureFlagChange {
  flag: DashboardAdminFeatureFlagDefinition;
  nextValue: boolean | null;
  organization: DashboardAdminOrganization;
  reason: string;
}

interface FeatureFlagOverrideSubmitInput {
  fetchImpl: typeof fetch;
  flagName: FeatureFlagName;
  organizationId: string;
  reason: string;
  value: boolean | null;
}

const overrideSelections: OverrideSelection[] = [
  "default",
  "enabled",
  "disabled",
];

export const AdminFeatureFlagsTab: FC = () => {
  const [state, setState] =
    useState<DashboardAdminFeatureFlagsListResponse | null>(null);
  const [selectedFlagName, setSelectedFlagName] =
    useState<FeatureFlagName | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [pendingChange, setPendingChange] =
    useState<PendingFeatureFlagChange | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const response = await fetch("/admin/feature-flags");
      if (!response.ok) {
        setErrorMessage(await response.text());
        return;
      }
      const data: DashboardAdminFeatureFlagsListResponse =
        await response.json();
      setState(data);
      setSelectedFlagName((current) =>
        current && data.flags.some((flag) => flag.flag_name === current)
          ? current
          : (data.flags[0]?.flag_name ?? null),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const selectedFlag = useMemo(
    () =>
      state?.flags.find((flag) => flag.flag_name === selectedFlagName) ?? null,
    [selectedFlagName, state],
  );

  const submitPendingChange = async () => {
    if (!pendingChange) {
      return;
    }
    setSaving(true);
    setErrorMessage(null);
    setSaved(false);
    try {
      await submitFeatureFlagOverride({
        fetchImpl: fetch,
        flagName: pendingChange.flag.flag_name,
        organizationId: pendingChange.organization.organization_id,
        reason: pendingChange.reason,
        value: pendingChange.nextValue,
      });
      setPendingChange(null);
      setSaved(true);
      await load();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PanelSection title="Feature Flags">
      <div className="space-y-4">
        <p className="font-mono text-[11px] text-slate-600 dark:text-slate-300">
          Manage per-organization feature flag overrides. Default removes the
          override and falls back to the tenant snapshot value.
        </p>
        {loading && <Alert variant="info">Loading feature flags...</Alert>}
        {errorMessage && <Alert variant="error">{errorMessage}</Alert>}
        {saved && <Alert variant="success">Feature flag override saved.</Alert>}
        {state && state.flags.length === 0 && (
          <Alert variant="info">No feature flags are registered.</Alert>
        )}
        {state && state.organizations.length === 0 && (
          <Alert variant="info">No organizations are available.</Alert>
        )}
        {state && state.flags.length > 0 && (
          <FeatureFlagSelector
            flags={state.flags}
            selectedFlagName={selectedFlagName}
            onSelect={setSelectedFlagName}
          />
        )}
        {state && selectedFlag && state.organizations.length > 0 && (
          <FeatureFlagsOrgTable
            flag={selectedFlag}
            overrides={state.overrides}
            organizations={state.organizations}
            onChange={(organization, nextValue) =>
              setPendingChange({
                flag: selectedFlag,
                nextValue,
                organization,
                reason: "",
              })
            }
            onCopy={(organizationId) =>
              void navigator.clipboard?.writeText(organizationId)
            }
          />
        )}
        <PendingFeatureFlagChangeDialog
          change={pendingChange}
          isProcessing={saving}
          onClose={() => setPendingChange(null)}
          onConfirm={() => void submitPendingChange()}
          onReasonChange={(reason) =>
            setPendingChange((current) =>
              current ? { ...current, reason } : current,
            )
          }
        />
      </div>
    </PanelSection>
  );
};

const FeatureFlagSelector: FC<{
  flags: readonly DashboardAdminFeatureFlagDefinition[];
  onSelect: (flagName: FeatureFlagName) => void;
  selectedFlagName: FeatureFlagName | null;
}> = ({ flags, onSelect, selectedFlagName }) => (
  <section className="border border-slate-700/70 bg-slate-950/40 p-3">
    <label className="space-y-1 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
      <span>Feature Flag</span>
      <select
        className="w-full border border-slate-700 bg-slate-900 px-3 py-2 text-sm normal-case tracking-normal text-slate-100 outline-none focus:border-cyan-400/70"
        onChange={(event) => {
          const flag = flags.find(
            (definition) => definition.flag_name === event.target.value,
          );
          if (flag) {
            onSelect(flag.flag_name);
          }
        }}
        value={selectedFlagName ?? ""}
      >
        {flags.map((flag) => (
          <option key={flag.flag_name} value={flag.flag_name}>
            {flag.label} ({flag.flag_name})
          </option>
        ))}
      </select>
    </label>
    {flags
      .filter((flag) => flag.flag_name === selectedFlagName)
      .map((flag) => (
        <div
          className="mt-3 grid gap-2 font-mono text-[11px] text-slate-400 md:grid-cols-2"
          key={flag.flag_name}
        >
          <div>
            <span className="text-slate-500">Description:</span>{" "}
            {flag.description}
          </div>
          <div>
            <span className="text-slate-500">Snapshot global default:</span>{" "}
            {formatBoolean(flag.snapshot_default_value)}
          </div>
        </div>
      ))}
  </section>
);

export const FeatureFlagsOrgTable: FC<{
  flag: DashboardAdminFeatureFlagDefinition;
  onChange: (
    organization: DashboardAdminOrganization,
    nextValue: boolean | null,
  ) => void;
  onCopy: (organizationId: string) => void;
  organizations: readonly DashboardAdminOrganization[];
  overrides: readonly DashboardAdminFeatureFlagOverride[];
}> = ({ flag, onChange, onCopy, organizations, overrides }) => {
  const overrideByOrganization = new Map(
    overrides
      .filter((override) => override.flag_name === flag.flag_name)
      .map((override) => [override.organization_id, override]),
  );

  return (
    <section className="overflow-x-auto border border-slate-700/70 bg-slate-950/40">
      <table className="min-w-full divide-y divide-slate-800 text-left font-mono text-[11px]">
        <thead className="bg-slate-950/60 uppercase tracking-[0.18em] text-slate-500">
          <tr>
            <th className="px-3 py-2">Organization</th>
            <th className="px-3 py-2">Org ID</th>
            <th className="px-3 py-2">Global Default</th>
            <th className="px-3 py-2">Override</th>
            <th className="px-3 py-2">Effective</th>
            <th className="px-3 py-2">Last Changed</th>
            <th className="px-3 py-2">Changed By</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800 text-slate-300">
          {organizations.map((organization) => (
            <FeatureFlagsOrgRow
              defaultValue={flag.snapshot_default_value}
              key={organization.organization_id}
              onChange={onChange}
              onCopy={onCopy}
              organization={organization}
              override={overrideByOrganization.get(
                organization.organization_id,
              )}
            />
          ))}
        </tbody>
      </table>
    </section>
  );
};

const FeatureFlagsOrgRow: FC<{
  defaultValue: boolean;
  onChange: (
    organization: DashboardAdminOrganization,
    nextValue: boolean | null,
  ) => void;
  onCopy: (organizationId: string) => void;
  organization: DashboardAdminOrganization;
  override: DashboardAdminFeatureFlagOverride | undefined;
}> = ({ defaultValue, onChange, onCopy, organization, override }) => {
  const currentValue = override?.value ?? null;
  const currentSelection = overrideSelectionFor(currentValue);
  const effectiveValue = override?.value ?? defaultValue;
  return (
    <tr>
      <td className="px-3 py-2">
        <div className="text-slate-100">{organization.name}</div>
        <div className="text-slate-500">{organization.slug}</div>
      </td>
      <td className="px-3 py-2">
        <OrganizationIdCell onCopy={onCopy} organization={organization} />
      </td>
      <td className="px-3 py-2">{formatBoolean(defaultValue)}</td>
      <td className="px-3 py-2">
        <OverrideSelector
          currentSelection={currentSelection}
          onChange={(nextValue) => onChange(organization, nextValue)}
        />
      </td>
      <td className="px-3 py-2">{formatBoolean(effectiveValue)}</td>
      <td className="px-3 py-2">
        {override ? formatTimestamp(override.set_at) : "—"}
      </td>
      <td className="px-3 py-2">
        {nonWhiteSpaceOr(override?.set_by_user_id, "—")}
      </td>
    </tr>
  );
};

const OrganizationIdCell: FC<{
  onCopy: (organizationId: string) => void;
  organization: DashboardAdminOrganization;
}> = ({ onCopy, organization }) => (
  <div className="flex items-center gap-2">
    <code title={organization.organization_id}>
      {shortOrganizationId(organization.organization_id)}
    </code>
    <Button
      onClick={() => onCopy(organization.organization_id)}
      size="sm"
      type="button"
      variant="ghost"
    >
      Copy
    </Button>
  </div>
);

const OverrideSelector: FC<{
  currentSelection: OverrideSelection;
  onChange: (nextValue: boolean | null) => void;
}> = ({ currentSelection, onChange }) => (
  <select
    className="border border-slate-700 bg-slate-900 px-2 py-1 text-slate-100 outline-none focus:border-cyan-400/70"
    data-current-selection={currentSelection}
    onChange={(event) => {
      const selection = parseOverrideSelection(event.target.value);
      if (selection !== currentSelection) {
        onChange(overrideValueFromSelection(selection));
      }
    }}
    value={currentSelection}
  >
    {overrideSelections.map((selection) => (
      <option key={selection} value={selection}>
        {formatOverrideSelection(selection)}
      </option>
    ))}
  </select>
);

export const PendingFeatureFlagChangeDialog: FC<{
  change: PendingFeatureFlagChange | null;
  isProcessing: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onReasonChange: (reason: string) => void;
}> = ({ change, isProcessing, onClose, onConfirm, onReasonChange }) => (
  <ConfirmationDialog
    body={
      change ? (
        <PendingFeatureFlagChangeBody
          change={change}
          onReasonChange={onReasonChange}
        />
      ) : null
    }
    confirmLabel="Save Override"
    isOpen={change !== null}
    isProcessing={isProcessing}
    onClose={onClose}
    onConfirm={onConfirm}
    title="Confirm feature flag change"
  />
);

export const PendingFeatureFlagChangeBody: FC<{
  change: PendingFeatureFlagChange;
  onReasonChange: (reason: string) => void;
}> = ({ change, onReasonChange }) => (
  <div className="space-y-3">
    <p>
      Change {change.flag.label} for {change.organization.name} to{" "}
      {formatOverrideValue(change.nextValue)}?
    </p>
    <label className="block space-y-1 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
      <span>Reason (optional)</span>
      <textarea
        className="min-h-20 w-full border border-slate-700 bg-slate-900 px-3 py-2 text-sm normal-case tracking-normal text-slate-100 outline-none focus:border-cyan-400/70"
        onChange={(event) => onReasonChange(event.target.value)}
        placeholder="Rollout, rollback, support request..."
        value={change.reason}
      />
    </label>
  </div>
);

export function buildFeatureFlagOverrideRequest(input: {
  reason: string;
  value: boolean | null;
}) {
  const reason = input.reason.trim();
  return reason ? { reason, value: input.value } : { value: input.value };
}

export async function submitFeatureFlagOverride(
  input: FeatureFlagOverrideSubmitInput,
) {
  // Call unbound: window.fetch throws "Illegal invocation" with `input` as receiver.
  const { fetchImpl } = input;
  const response = await fetchImpl(
    `/admin/feature-flags/${encodeURIComponent(input.flagName)}/organizations/${encodeURIComponent(input.organizationId)}`,
    {
      body: JSON.stringify(
        buildFeatureFlagOverrideRequest({
          reason: input.reason,
          value: input.value,
        }),
      ),
      headers: { "content-type": "application/json" },
      method: "PUT",
    },
  );
  if (!response.ok) {
    throw new Error(await response.text());
  }
}

export function overrideSelectionFor(value: boolean | null): OverrideSelection {
  if (value === true) {
    return "enabled";
  }
  if (value === false) {
    return "disabled";
  }
  return "default";
}

export function overrideValueFromSelection(
  selection: OverrideSelection,
): boolean | null {
  switch (selection) {
    case "default": {
      return null;
    }
    case "enabled": {
      return true;
    }
    case "disabled": {
      return false;
    }
    default: {
      throw assertUnreachable(selection);
    }
  }
}

function parseOverrideSelection(value: string): OverrideSelection {
  const match = overrideSelections.find((selection) => selection === value);
  return match ?? "default";
}

function formatOverrideSelection(selection: OverrideSelection): string {
  switch (selection) {
    case "default": {
      return "Default";
    }
    case "enabled": {
      return "Enabled";
    }
    case "disabled": {
      return "Disabled";
    }
    default: {
      throw assertUnreachable(selection);
    }
  }
}

function formatOverrideValue(value: boolean | null): string {
  if (value === true) {
    return "Enabled";
  }
  if (value === false) {
    return "Disabled";
  }
  return "Default";
}

function formatBoolean(value: boolean): string {
  return value ? "Enabled" : "Disabled";
}

function shortOrganizationId(organizationId: string): string {
  return organizationId.length > 12
    ? `${organizationId.slice(0, 8)}…`
    : organizationId;
}

function formatTimestamp(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? value : date.toLocaleString();
}
